import type {NatsConnection} from "nats.ws";
import type React from "react";
import {createContext, useContext, useEffect, useRef, useState} from "react";
import {STORAGE_KEYS} from "@/common/constants/storage-keys";
import {LocalStorage} from "@/common/lib/localstorage";
import {initializeNatsConnection, publishNatsMessage, subscribeToNatsTopic,} from "@/common/lib/nats";

type Message = { topic: string; payload: Uint8Array };
type Subscription = {
    unlisten: () => Promise<void>;
    callbacks: Set<(msg: Uint8Array) => void>;
};

interface NatsContextValue {
    subscribe: (topic: string, callback: (msg: Uint8Array) => void) => void;
    unsubscribe: (topic: string, callback: (msg: Uint8Array) => void) => void;
    publish: (topic: string, message: Uint8Array) => void;
    lastMessage?: Message | null;
}

const NatsContext = createContext<NatsContextValue | undefined>(undefined);

export function NatsProvider({children}: { children: React.ReactNode }) {
    // State to hold the NATS connection instance, initialized to null. This will be set once the connection is established.
    const [nc, setNc] = useState<NatsConnection | null>(null);

    // State to hold the last received message, initialized to null. This will be updated whenever a new message is received.
    const [lastMessage, setLastMessage] = useState<Message | null>(null);

    // Ref to store active subscriptions, using a Map to associate topics with their corresponding Subscription objects.
    const subscriptions = useRef<Map<string, Subscription>>(new Map());

    useEffect(() => {
        async function setup() {
            const brokerUrl = LocalStorage.get(STORAGE_KEYS.NATS_BROKER_URL);

            if (!brokerUrl) {
                console.warn("NATS broker URL is not set in local storage.");
                return;
            }

            try {
                const connection = await initializeNatsConnection(brokerUrl);
                setNc(connection);
            } catch (e) {
                console.error("Failed to initialize NATS:", e);
            }
        }

        setup().then();
    }, []);

    function subscribe(
        topic: string,
        callback: (protoMsg: Uint8Array) => void
    ) {
        const existingSub = subscriptions.current.get(topic);

        if (!nc) {
            console.error("NATS connection is not established.");
            return;
        }

        if (existingSub) {
            existingSub.callbacks.add(callback);
        } else {
            const callbacks = new Set<(protoMsg: Uint8Array) => void>([
                callback,
            ]);

            subscribeToNatsTopic(nc, topic, (payload) => {
                callbacks.forEach((cb) => cb(payload));
                setLastMessage({topic, payload});
            })
                .then((unlisten) => {
                    subscriptions.current.set(topic, {unlisten, callbacks});
                })
                .catch((e) => {
                    console.error(`Failed to subscribe to ${topic}:`, e);
                });
        }
    }

    function unsubscribe(
        topic: string,
        callback: (protoMsg: Uint8Array) => void
    ) {
        const sub = subscriptions.current.get(topic);
        if (!sub) return;

        sub.callbacks.delete(callback);

        if (sub.callbacks.size === 0) {
            sub.unlisten().then(() => {
                subscriptions.current.delete(topic);
            });
        }
    }

    function publish(topic: string, protoMsg: Uint8Array) {
        if (!nc) {
            console.error("NATS connection is not established.");
            return;
        }
        try {
            publishNatsMessage(nc, topic, protoMsg);
        } catch (e) {
            console.error("Failed to publish message:", e);
        }
    }

    return (
        <NatsContext.Provider
            value={{subscribe, unsubscribe, publish, lastMessage}}
        >
            {children}
        </NatsContext.Provider>
    );
}

export function useNats() {
    const context = useContext(NatsContext);
    if (!context) {
        throw new Error("useNats must be used within a NatsProvider");
    }
    return context;
}
